<template>
  <div ref="container" class="relative h-full w-full">
    <div
      v-for="positionedItem in positionedItems"
      :key="positionedItem.key"
      class="absolute -translate-x-1/2 -translate-y-1/2"
      :style="{
        width: `${itemSize}px`,
        height: `${itemSize}px`,
        left: `${positionedItem.x}px`,
        top: `${positionedItem.y}px`,
      }"
    >
      <slot
        :item="positionedItem.item"
        :index="positionedItem.index"
        :x="positionedItem.x"
        :y="positionedItem.y"
      />
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed, ref } from 'vue'
import { useElementBounding } from '@vueuse/core'

interface PositionedItem {
  item: T
  index: number
  key: string | number
  x: number
  y: number
}

interface Props {
  items: ReadonlyArray<T>
  itemSize?: number
  padding?: number
  maxAttempts?: number
  itemKey?: (item: T, index: number) => string | number
}

const { items, itemSize = 40, padding = 12, maxAttempts = 120, itemKey } = defineProps<Props>()

defineSlots<{
  default(props: { item: T; index: number; x: number; y: number }): void
}>()

const container = ref<HTMLElement | null>(null)
const { width, height } = useElementBounding(container)

const minDistance = computed(() => itemSize + padding)

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const collides = (x: number, y: number, positioned: PositionedItem[]) =>
  positioned.some((placed) => {
    const dx = x - placed.x
    const dy = y - placed.y
    return dx * dx + dy * dy < minDistance.value * minDistance.value
  })

const positionedItems = computed<PositionedItem[]>(() => {
  const placed: PositionedItem[] = []
  const currentWidth = width.value
  const currentHeight = height.value

  if (currentWidth <= 0 || currentHeight <= 0) {
    return []
  }

  const halfSize = itemSize / 2
  const centerX = currentWidth / 2
  const centerY = currentHeight / 2

  items.forEach((item, index) => {
    let x = centerX
    let y = centerY

    for (let attempt = 0; attempt < maxAttempts && collides(x, y, placed); attempt++) {
      const angle = Math.random() * Math.PI * 2
      const radius = Math.min(8 + attempt * 2, Math.min(currentWidth, currentHeight) / 2)
      const candidateX = centerX + Math.cos(angle) * radius
      const candidateY = centerY + Math.sin(angle) * radius

      x = clamp(candidateX, halfSize, currentWidth - halfSize)
      y = clamp(candidateY, halfSize, currentHeight - halfSize)
    }

    placed.push({
      item,
      index,
      key: itemKey?.(item, index) ?? index,
      x,
      y,
    })
  })

  return placed
})
</script>
